/**
 * ReportContent Store
 *
 * Holds the in-progress Seeq content (worksheet screenshot or interactive visualization) that the user is inserting
 * or editing in the Report Content modal. Not persisted: content parameters must never leak between documents
 * (CRAB-23743), because date ranges and asset selections belong to a single document.
 *
 */

import _ from 'lodash';
import { createSelector } from 'reselect';
import {
  DEFAULT_HEIGHT,
  DEFAULT_SHAPE_KEY,
  DEFAULT_SIZE_KEY,
  DEFAULT_WIDTH,
  REPORT_CONTENT,
} from '@/reportEditor/report.constants';
import { toNumber } from '@/utilities/numberHelper.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type {
  CONTENT_STATE,
  DateRange,
  ReportContentShape,
  ReportContentSize,
  ReportContentSummary,
} from '@/reportEditor/report.constants';

export interface ReportContentState {
  /** ID of the saved content, or `undefined` for new content not yet saved to the backend. */
  id: string | undefined;
  /** Which step of the Report Content modal is open, or `''` when the modal is closed. */
  modalName: CONTENT_STATE | '';
  /** ID of the report date range the content uses, or `undefined` to inherit the worksheet's range. */
  dateRangeId: string | undefined;
  /** ID of the report asset selection the content uses, or `undefined` to inherit the worksheet's asset. */
  assetSelectionId: string | number | undefined;
  /** Content width, in pixels, or `undefined` while the user has cleared a custom width. */
  width: number | undefined;
  /** Content height, in pixels, or `undefined` while the user has cleared a custom height. */
  height: number | undefined;
  /** Key of the selected `REPORT_CONTENT.SIZE` preset. */
  sizeKey: string;
  /** Key of the selected `REPORT_CONTENT.SHAPE` preset. */
  shapeKey: string;
  /** Key of the selected `REPORT_CONTENT.SCALE` preset. */
  scale: number;
  /** True when the screenshot is sized to the rendered content instead of the user's size/shape. */
  useSizeFromRender: boolean;
  /** True when the source worksheet supports interactive (React) content. */
  canUseReact: boolean | undefined;
  /** True when the content renders interactively instead of as an image. */
  isReact: boolean | undefined;
  /** Analysis view (e.g. Trend, Statistics Summary) the content captures. */
  analysisViewType: string | undefined;
  /** Summarization applied to the content, or `undefined` to inherit it. */
  summary: ReportContentSummary | undefined;
  /** True when the size preset is fill-to-fit. */
  fillToFit: boolean;
  /** True to hide, false to show, or null to inherit the worksheet's uncertainty setting. */
  hideUncertainty: boolean | null | undefined;
  /** ID of the source workbook. */
  workbookId: string | undefined;
  /** Type of the source workbook (e.g. Analysis, Vantage). */
  workbookType: string | undefined;
  /** ID of the source worksheet. */
  worksheetId: string | undefined;
  /** ID of the source workstep. */
  workstepId: string | undefined;
  /** URL of the source worksheet, including any override parameters. */
  sourceUrl: string | undefined;
}

const initialState: ReportContentState = Object.freeze({
  id: undefined,
  modalName: '',
  dateRangeId: undefined,
  assetSelectionId: undefined,
  width: DEFAULT_WIDTH,
  height: DEFAULT_HEIGHT,
  sizeKey: DEFAULT_SIZE_KEY.key,
  shapeKey: DEFAULT_SHAPE_KEY.key,
  scale: REPORT_CONTENT.SCALE.NORMAL.key,
  useSizeFromRender: false,
  canUseReact: false,
  isReact: true,
  analysisViewType: undefined,
  summary: undefined,
  fillToFit: false,
  hideUncertainty: null,
  workbookId: undefined,
  workbookType: undefined,
  worksheetId: undefined,
  workstepId: undefined,
  sourceUrl: undefined,
});

export interface ReportContentActions {
  /** Sets the ID of the saved content; `undefined` marks it as new, unsaved content. */
  setContentId: (id: string | undefined) => void;
  /** Sets the source workbook and its type together, since the type is only meaningful for that workbook. */
  setWorkbookId: (workbookId: string | undefined, workbookType: string | undefined) => void;
  /** Sets the source worksheet. */
  setWorksheetId: (worksheetId: string | undefined) => void;
  /** Sets the source workstep. */
  setWorkstepId: (workstepId: string | undefined) => void;
  /** Sets the width in pixels. Accepts input text; a blank or non-numeric value clears it to `undefined`. */
  setWidth: (width: number | string | undefined) => void;
  /** Sets the height in pixels. Accepts input text; a blank or non-numeric value clears it to `undefined`. */
  setHeight: (height: number | string | undefined) => void;
  /** Selects a `REPORT_CONTENT.SIZE` preset and derives `fillToFit` from it. */
  setSizeKey: (sizeKey: string) => void;
  /** Selects a `REPORT_CONTENT.SHAPE` preset. */
  setShapeKey: (shapeKey: string) => void;
  /** Selects a `REPORT_CONTENT.SCALE` preset. Ignores values that are not a scale key. */
  setScale: (scale: number | string) => void;
  /** Sets the report date range the content uses; an empty value inherits the worksheet's range. */
  setDateRangeId: (dateRangeId: string | undefined) => void;
  /** Sets the report asset selection the content uses; an empty value inherits the worksheet's asset. */
  setAssetSelectionId: (assetSelectionId: string | number | null | undefined) => void;
  /** Sets the source worksheet URL, including any override parameters. */
  setSourceUrl: (sourceUrl: string | undefined) => void;
  /**
   * Sets whether the screenshot is sized to the rendered content. Switching back to user sizing restores the default
   * size and shape.
   */
  setUseSizeFromRender: (useSizeFromRender: boolean) => void;
  /** Sets whether the source worksheet supports interactive content. */
  setCanUseReact: (canUseReact: boolean | undefined) => void;
  /** Sets whether the content renders interactively instead of as an image. */
  setIsReact: (isReact: boolean | undefined) => void;
  /** Sets whether uncertainty is hidden in the content. */
  setHideUncertainty: (hideUncertainty: boolean | null | undefined) => void;
  /** Sets the analysis view (e.g. Trend, Statistics Summary) the content captures. */
  setAnalysisViewType: (analysisViewType: string) => void;
  /** Sets the summarization applied to the content; `undefined` inherits it. */
  setSummary: (summary: ReportContentSummary | undefined) => void;
  /** Opens a step of the Report Content modal, or closes it with `''`. */
  setModalName: (modalName: CONTENT_STATE | '') => void;
  /**
   * Clears `dateRangeId` when `dateRange` is the content's date range and it has been archived. Called whenever the
   * report stores a date range, so content never references an archived range.
   */
  clearDateRangeIdIfArchived: (dateRange: Partial<Pick<DateRange, 'id' | 'isArchived'>>) => void;
  /**
   * Clears the loaded content but keeps its display parameters, so the next new content defaults to the date range,
   * asset selection, size, shape, scale, summary, and view of the last content.
   */
  clear: () => void;
  /** Restores the initial state, with `overrides` applied on top. Unlike `clear`, keeps nothing from the last content. */
  reset: (overrides?: Partial<ReportContentState>) => void;
}

const reportContentActionsFactory: ActionsFactory<ReportContentState, ReportContentActions> = (
  set,
  get,
  resetState,
) => ({
  setContentId: (id) =>
    set((draft) => {
      draft.id = id;
    }),

  setWorkbookId: (workbookId, workbookType) =>
    set((draft) => {
      draft.workbookId = workbookId;
      draft.workbookType = workbookType;
    }),

  setWorksheetId: (worksheetId) =>
    set((draft) => {
      draft.worksheetId = worksheetId;
    }),

  setWorkstepId: (workstepId) =>
    set((draft) => {
      draft.workstepId = workstepId;
    }),

  setWidth: (width) =>
    set((draft) => {
      draft.width = toNumber(width);
    }),

  setHeight: (height) =>
    set((draft) => {
      draft.height = toNumber(height);
    }),

  setSizeKey: (sizeKey) =>
    set((draft) => {
      draft.fillToFit = sizeKey === REPORT_CONTENT.SIZE.FILL_TO_FIT.key;
      draft.sizeKey = sizeKey;
    }),

  setShapeKey: (shapeKey) =>
    set((draft) => {
      draft.shapeKey = shapeKey;
    }),

  setScale: (scale) =>
    set((draft) => {
      const scaleKey = toNumber(scale);
      if (scaleKey !== undefined && _.some(REPORT_CONTENT.SCALE, { key: scaleKey })) {
        draft.scale = scaleKey;
      }
    }),

  setDateRangeId: (dateRangeId) =>
    set((draft) => {
      draft.dateRangeId = dateRangeId || undefined;
    }),

  setAssetSelectionId: (assetSelectionId) =>
    set((draft) => {
      draft.assetSelectionId = assetSelectionId || undefined;
    }),

  setSourceUrl: (sourceUrl) =>
    set((draft) => {
      draft.sourceUrl = sourceUrl;
    }),

  setUseSizeFromRender: (useSizeFromRender) =>
    set((draft) => {
      if (draft.useSizeFromRender && !useSizeFromRender) {
        draft.height = DEFAULT_HEIGHT;
        draft.width = DEFAULT_WIDTH;
        draft.sizeKey = DEFAULT_SIZE_KEY.key;
        draft.shapeKey = DEFAULT_SHAPE_KEY.key;
      }
      draft.useSizeFromRender = !!useSizeFromRender;
    }),

  setCanUseReact: (canUseReact) =>
    set((draft) => {
      draft.canUseReact = canUseReact;
    }),

  setIsReact: (isReact) =>
    set((draft) => {
      draft.isReact = isReact;
    }),

  setHideUncertainty: (hideUncertainty) =>
    set((draft) => {
      draft.hideUncertainty = hideUncertainty;
    }),

  setAnalysisViewType: (analysisViewType) =>
    set((draft) => {
      draft.analysisViewType = analysisViewType;
    }),

  setSummary: (summary) =>
    set((draft) => {
      draft.summary = summary;
    }),

  setModalName: (modalName) =>
    set((draft) => {
      draft.modalName = modalName;
    }),

  clearDateRangeIdIfArchived: (dateRange) =>
    set((draft) => {
      if (dateRange.isArchived && dateRange.id === draft.dateRangeId) {
        draft.dateRangeId = undefined;
      }
    }),

  clear: () => {
    const {
      dateRangeId,
      assetSelectionId,
      width,
      height,
      sizeKey,
      shapeKey,
      scale,
      analysisViewType,
      summary,
      fillToFit,
      hideUncertainty,
    } = get();
    resetState({
      dateRangeId,
      assetSelectionId,
      width,
      height,
      sizeKey,
      shapeKey,
      scale,
      analysisViewType,
      summary,
      fillToFit,
      hideUncertainty,
    });
  },

  reset: resetState,
});

const STORE_NAME = 'ReportContent';

const [ReportContentStoreProvider, useReportContentStoreInstance, useReportContentSelector] = createStore(
  STORE_NAME,
  initialState,
  reportContentActionsFactory,
);

export { ReportContentStoreProvider };

/** The `REPORT_CONTENT.SIZE` preset for a size key, or `undefined` if the key is unknown. */
export const getReportContentSizeConstant = (sizeKey: string): ReportContentSize | undefined =>
  Object.values(REPORT_CONTENT.SIZE).find((size) => size.key === sizeKey);

/** The `REPORT_CONTENT.SHAPE` preset for a shape key, or `undefined` if the key is unknown. */
export const getReportContentShapeConstant = (shapeKey: string): ReportContentShape | undefined =>
  Object.values(REPORT_CONTENT.SHAPE).find((shape) => shape.key === shapeKey);

export const useReportContentModalName = () => useReportContentSelector((state) => state.modalName ?? '');
export const useReportContentSourceUrl = () => useReportContentSelector((state) => state.sourceUrl ?? '');
export const useReportContentWorkbookId = () => useReportContentSelector((state) => state.workbookId);
export const useReportContentWorkbookType = () => useReportContentSelector((state) => state.workbookType);
export const useReportContentWorksheetId = () => useReportContentSelector((state) => state.worksheetId);
export const useReportContentWorkstepId = () => useReportContentSelector((state) => state.workstepId);
export const useReportContentWidth = () => useReportContentSelector((state) => state.width);
export const useReportContentHeight = () => useReportContentSelector((state) => state.height);
export const useReportContentSizeKey = () => useReportContentSelector((state) => state.sizeKey);
export const useReportContentShapeKey = () => useReportContentSelector((state) => state.shapeKey);
export const useReportContentScale = () => useReportContentSelector((state) => state.scale);
export const useReportContentSummary = () => useReportContentSelector((state) => state.summary);
export const useReportContentDateRangeId = () => useReportContentSelector((state) => state.dateRangeId);
export const useReportContentAnalysisViewType = () => useReportContentSelector((state) => state.analysisViewType);
export const useReportContentUseSizeFromRender = () =>
  useReportContentSelector((state) => state.useSizeFromRender ?? false);
export const useReportContentCanUseReact = () => useReportContentSelector((state) => state.canUseReact ?? false);
export const useReportContentIsReact = () => useReportContentSelector((state) => state.isReact ?? false);
export const useReportContentHideUncertainty = () => useReportContentSelector((state) => state.hideUncertainty ?? null);

/** The asset selection ID as a string, since numeric IDs can arrive from older content. */
export const useReportContentAssetSelectionId = () =>
  useReportContentSelector((state) => state.assetSelectionId?.toString());

const selectSizeConstant = createSelector((state: ReportContentState) => state.sizeKey, getReportContentSizeConstant);
export const useReportContentSizeConstant = () => useReportContentSelector(selectSizeConstant);

const selectShapeConstant = createSelector(
  (state: ReportContentState) => state.shapeKey,
  getReportContentShapeConstant,
);
export const useReportContentShapeConstant = () => useReportContentSelector(selectShapeConstant);

export const useReportContentActions = () => useReportContentStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateReportContentStore(initialStateOverrides?: Partial<ReportContentState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    reportContentActionsFactory,
    { storeName: STORE_NAME },
  );
}
